var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { TimeoutError } from "./errors/timeout-error";
import { isMoopsyError } from "../lib/is-moopsy-error";
import { TypedEventEmitterV3 } from "@moopsyjs/toolkit";
import { MoopsyRequest } from "./request";
import { TransportStatus } from "./transports/base";
function determineSideEffects(querySideEffects) {
    return querySideEffects
        ? querySideEffects.filter((sideEffect) => sideEffect != null).map((query, index) => ({
            sideEffectId: index,
            method: query._.endpoint,
            params: query._.params,
        }))
        : [];
}
const NO_RESPONSE = Symbol("NO_RESPONSE");
export class MutationCall {
    constructor(mutation) {
        this.mutation = mutation;
        this.failed = false;
        this.emitter = new TypedEventEmitterV3();
        this._called = false;
        this.response = NO_RESPONSE;
        this._debug = (...args) => {
            this.mutation.client._debug(`[MutationCall::${this.callId.slice(0, 4)}]`, ...args);
        };
        this.onSuccess = (cb) => {
            this.emitter.on("response", cb);
            return this;
        };
        this.onFailure = (cb) => {
            this.emitter.on("error", cb);
            return this;
        };
        this.awaitResult = () => {
            if (this.response !== NO_RESPONSE) {
                return Promise.resolve(this.response);
            }
            return new Promise((resolve) => {
                this.onSuccess(resolve);
                this.onFailure(resolve);
            });
        };
        this.declareFailure = (error) => {
            if (this.failed)
                return;
            this.failed = true;
            this.emitter.emit("error", error);
        };
        this.callMutation = (params) => __awaiter(this, void 0, void 0, function* () {
            if (this.called) {
                throw new Error("MutationCall.call() was called more than once.");
            }
            this.called = true;
            const sideEffects = determineSideEffects(this.mutation.querySideEffects);
            const message = {
                method: this.mutation.plug.Endpoint,
                params,
                callId: this.callId,
                sideEffects,
            };
            if (this.mutation.client.getTransportStatus() !== TransportStatus.connected) {
                this._debug("Transport is not connected, will await connection before sending");
                yield this.mutation.client.awaitConnected();
            }
            const timeout = setTimeout(() => {
                this._debug("Request timed out waiting for a response from the server");
                this.declareFailure(new TimeoutError(this.mutation.plug.Endpoint));
            }, this.mutation.timeout);
            this.mutation.client.activeCalls.add(this);
            const makeInactive = () => {
                this.mutation.client.activeCalls.delete(this);
                clearTimeout(timeout);
            };
            this.onSuccess(makeInactive);
            this.onFailure(makeInactive);
            this._debug(`Sending call for ${this.mutation.plug.Endpoint}`);
            this.mutation.client.send(new MoopsyRequest({
                event: "call",
                data: message
            }, this.mutation.plug.isPublic !== true, !this.mutation.isQuery));
            return this;
        });
        this.callId = this.mutation.client.generateId();
        this.mutation.client.incomingMessageEmitter.on(`response.${this.callId}`, (data) => {
            if (!this.failed) {
                if (isMoopsyError(data)) {
                    this._debug(`Received error for ${this.mutation.plug.Endpoint}`, data);
                    this.declareFailure(data);
                }
                else {
                    this._debug(`Received response for ${this.mutation.plug.Endpoint}`);
                    this.emitter.emit("response", data);
                    this.response = data;
                }
            }
        });
    }
    get called() {
        return this._called;
    }
    set called(value) {
        this._called = value;
    }
}
